import React, { useEffect, useRef, useState, useCallback } from 'react';
import classNames from 'classnames';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import ReactPlayer from 'react-player/vimeo';
import AuthorLink from './author-link';
import VimeoPlay from '../../assets/svgs/vimeo-play.svg';
import VimeoClose from '../../assets/svgs/vimeo-close.svg';
import VimeoPause from '../../assets/svgs/vimeo-pause.svg';
import VimeoVolume from '../../assets/svgs/vimeo-volume.svg';
import VimeoMute from '../../assets/svgs/vimeo-mute.svg';
import VimeoFullscreen from '../../assets/svgs/vimeo-fullscreen.svg';
import VimeoFullscreenRevert from '../../assets/svgs/vimeo-fullscreen-revert.svg';
import { convertToTimeString } from '../../model/post';
import { isBrowser } from '../../model/util';

const VimeoPlayer = ({
  className,
  videoCode,
  videoTitle,
  videoPostAuthors,
  videoAuthor,
  videoDuration,
  onClose,
}) => {
  const videoContainerRef = useRef();
  const videoPlayerRef = useRef();
  const hideTimeoutRef = useRef();

  const inactiveTimeoutLength = 4000;
  const [forceVisibleControls, setForceVisibleControls] = useState(false);
  const [hasHiddenControls, setHasHiddenControls] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoIsPlaying, setVideoIsPlaying] = useState(true);
  const [videoMuted, setVideoMuted] = useState(false);
  const [videoTimeRemaining, setVideoTimeRemaining] = useState('');

  const startHideTimeout = () => {
    clearTimeout(hideTimeoutRef.current);
    hideTimeoutRef.current = setTimeout(() => {
      setHasHiddenControls(true);
    }, inactiveTimeoutLength);
  };

  const onControlsSetPausePlay = () => {
    setVideoIsPlaying((playing) => !playing);
  };

  const onControlsSetVolume = () => {
    setVideoMuted((muted) => !muted);
  };

  const onControlsProgress = (to) => {
    setVideoProgress(to);
  };

  const onFullscreenClick = () => {
    if (videoContainerRef.current) {
      if (!document.fullscreenElement) {
        try {
          videoContainerRef.current.requestFullscreen();
          setIsFullscreen(true);
        } catch (e) {}
      } else {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  useEffect(() => {
    const currentTime = videoPlayerRef.current.getCurrentTime();
    const timeRemaining = videoDuration - currentTime;
    setVideoTimeRemaining(convertToTimeString(timeRemaining));
  }, [videoPlayerRef, videoProgress, videoDuration]);

  const onEscapeKeyDown = useCallback(
    (e) => {
      if (e.keyCode === 27) {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (!isBrowser) return;

    window.addEventListener('keydown', onEscapeKeyDown);

    return () => {
      window.removeEventListener('keydown', onEscapeKeyDown);
    };
  }, [onEscapeKeyDown]);

  return (
    <div
      className={classNames(className, 'react-vimeo-player')}
      ref={videoContainerRef}
      onMouseMove={(e) => {
        if (hasHiddenControls) {
          setHasHiddenControls(false);
        }

        startHideTimeout();
      }}
      role="presentation"
    >
      <VimeoClose
        className={classNames([
          'react-vimeo-player__close',
          hasHiddenControls && 'react-vimeo-player__close--hidden',
        ])}
        onClick={onClose}
      />
      <ReactPlayer
        className="react-vimeo-player__video"
        ref={videoPlayerRef}
        url={`https://vimeo.com/${videoCode}`}
        playing={videoIsPlaying}
        controls={false}
        volume={1}
        muted={videoMuted}
        onPlay={() => {
          startHideTimeout();
        }}
        onProgress={({ played }) => {
          onControlsProgress(played.toFixed(6) * 100);
        }}
        onClick={onControlsSetPausePlay}
        width={''}
        height={''}
      />
      <div
        className={classNames([
          'react-vimeo-player__player',
          hasHiddenControls && !forceVisibleControls && 'react-vimeo-player__player--hidden',
        ])}
      >
        {/* TODO: add current time to top of slider handle */}
        {/*<div className="react-vimeo-player__player-current-time">*/}
        {/*    {videoCurrentTime}*/}
        {/*</div>*/}
        <div className="react-vimeo-player__player-authors">
          Directed by&nbsp;
          {videoPostAuthors && videoPostAuthors.length > 0
            ? videoPostAuthors.map((author, idx) => (
                <AuthorLink author={author} postAuthors={videoPostAuthors} index={idx} key={idx} />
              ))
            : videoAuthor}
        </div>
        <div className="react-vimeo-player__player-title">{videoTitle}</div>
        <div
          className="react-vimeo-player__player-controls"
          onMouseEnter={() => setForceVisibleControls(true)}
          onMouseLeave={() => setForceVisibleControls(false)}
          role="presentation"
        >
          <button
            className="react-vimeo-player__player-controls-play-pause"
            onClick={onControlsSetPausePlay}
          >
            {videoIsPlaying ? <VimeoPause /> : <VimeoPlay />}
          </button>
          <div className="react-vimeo-player__player-controls-time-left">{videoTimeRemaining}</div>
          <Slider
            value={videoProgress}
            step={0.001}
            onChange={(to) => {
              if (videoPlayerRef.current) {
                const toDecimal = to / 100;
                videoPlayerRef.current.seekTo(toDecimal);
                onControlsProgress(to);
              }
            }}
          />
          <button
            className="react-vimeo-player__player-controls-volume"
            onClick={onControlsSetVolume}
          >
            {videoMuted ? <VimeoMute /> : <VimeoVolume />}
          </button>
          <div className="react-vimeo-player__player-controls-fullscreen">
            {!isFullscreen && <VimeoFullscreen onClick={onFullscreenClick} />}
            {isFullscreen && <VimeoFullscreenRevert onClick={onFullscreenClick} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VimeoPlayer;
